import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  TextField,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Save,
  Cancel,
  Add,
  Delete,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { botApi } from '../services/api';
import { BotGroupsConfig, GroupConfig } from '../types';

interface ConfigEditorProps {
  open: boolean;
  onClose: () => void;
  botName: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ConfigEditor: React.FC<ConfigEditorProps> = ({ open, onClose, botName }) => {
  const [tabValue, setTabValue] = useState(0);
  const [editedConfig, setEditedConfig] = useState<BotGroupsConfig | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newOrtaklikInputs, setNewOrtaklikInputs] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  // Fetch config data
  const { data: config, isLoading, error } = useQuery({
    queryKey: ['botConfig', botName],
    queryFn: () => botApi.getBotConfig(botName),
    enabled: open && !!botName,
  });

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: (newConfig: BotGroupsConfig) => botApi.updateBotConfig(botName, newConfig),
    onSuccess: () => {
      toast.success('Konfigürasyon başarıyla güncellendi');
      queryClient.invalidateQueries({ queryKey: ['botConfig', botName] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Konfigürasyon güncellenemedi: ${error.message}`);
    },
  });

  useEffect(() => {
    if (config) {
      setEditedConfig(JSON.parse(JSON.stringify(config)));
    }
  }, [config]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = () => {
    if (editedConfig) {
      updateConfigMutation.mutate(editedConfig);
    }
  };

  const handleAddGroup = () => {
    if (newGroupName && editedConfig) {
      const newGroup: GroupConfig = {
        chat_id: '',
        topic_id: 0,
        name: newGroupName,
        ortakliklar: []
      };
      
      setEditedConfig({
        ...editedConfig,
        groups: {
          ...editedConfig.groups,
          [newGroupName.toLowerCase().replace(/\s+/g, '_')]: newGroup
        }
      });
      
      setNewGroupName('');
      toast.success('Yeni grup eklendi');
    }
  };

  const handleDeleteGroup = (groupKey: string) => {
    if (editedConfig) {
      const { [groupKey]: deleted, ...remainingGroups } = editedConfig.groups;
      setEditedConfig({
        ...editedConfig,
        groups: remainingGroups
      });
      toast.success('Grup silindi');
    }
  };

  const handleUpdateGroup = (groupKey: string, updatedGroup: GroupConfig) => {
    if (editedConfig) {
      setEditedConfig({
        ...editedConfig,
        groups: {
          ...editedConfig.groups,
          [groupKey]: updatedGroup
        }
      });
    }
  };

  const handleAddOrtaklik = (groupKey: string) => {
    const newOrtaklik = newOrtaklikInputs[groupKey] || '';
    if (newOrtaklik && editedConfig) {
      const group = editedConfig.groups[groupKey];
      const updatedGroup = {
        ...group,
        ortakliklar: [...group.ortakliklar, newOrtaklik]
      };
      handleUpdateGroup(groupKey, updatedGroup);
      setNewOrtaklikInputs(prev => ({ ...prev, [groupKey]: '' }));
      toast.success('Ortaklık eklendi');
    }
  };

  const handleDeleteOrtaklik = (groupKey: string, ortaklikIndex: number) => {
    if (editedConfig) {
      const group = editedConfig.groups[groupKey];
      const updatedOrtakliklar = group.ortakliklar.filter((_, index) => index !== ortaklikIndex);
      const updatedGroup = {
        ...group,
        ortakliklar: updatedOrtakliklar
      };
      handleUpdateGroup(groupKey, updatedGroup);
      toast.success('Ortaklık silindi');
    }
  };

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Konfigürasyon yükleniyor...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error">
            Konfigürasyon yüklenirken hata oluştu: {(error as any).message}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Kapat</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.3)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.5rem',
        borderRadius: '20px 20px 0 0',
      }}>
        🤖 {botName.toUpperCase()} - Konfigürasyon Düzenleyici
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          background: 'linear-gradient(90deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
          borderRadius: '15px 15px 0 0',
          mb: 2
        }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 'bold',
                fontSize: '1rem',
                '&.Mui-selected': {
                  color: '#667eea',
                }
              },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(90deg, #667eea, #764ba2)',
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab label="📁 Gruplar" />
            <Tab label="📄 JSON Görünüm" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Add New Group */}
          <Paper sx={{ 
            p: 3, 
            mb: 3,
            background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
            borderRadius: '15px',
            border: '1px solid rgba(102,126,234,0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}>
            <Typography variant="h6" gutterBottom sx={{ 
              color: '#667eea',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              ➕ Yeni Grup Ekle
              Yeni Grup Ekle
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                size="small"
                label="Grup Adı"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddGroup()}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddGroup}
                disabled={!newGroupName}
              >
                Ekle
              </Button>
            </Box>
          </Paper>

          {/* Groups List */}
          {editedConfig && Object.entries(editedConfig.groups).map(([groupKey, group]) => (
            <Paper key={groupKey} sx={{ p: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {group.name}
                </Typography>
                <IconButton
                  color="error"
                  onClick={() => handleDeleteGroup(groupKey)}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Box>

              <Box display="flex" gap={2} mb={2}>
                <TextField
                  size="small"
                  label="Chat ID"
                  value={group.chat_id}
                  onChange={(e) => handleUpdateGroup(groupKey, { ...group, chat_id: e.target.value })}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Topic ID"
                  type="number"
                  value={group.topic_id}
                  onChange={(e) => handleUpdateGroup(groupKey, { ...group, topic_id: parseInt(e.target.value) || 0 })}
                  sx={{ width: 120 }}
                />
                <TextField
                  size="small"
                  label="Grup Adı"
                  value={group.name}
                  onChange={(e) => handleUpdateGroup(groupKey, { ...group, name: e.target.value })}
                  sx={{ flex: 1 }}
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Ortaklıklar ({group.ortakliklar.length})
              </Typography>

              <Box display="flex" gap={1} mb={2}>
                <TextField
                  size="small"
                  label="Yeni Ortaklık"
                  value={newOrtaklikInputs[groupKey] || ''}
                  onChange={(e) => setNewOrtaklikInputs(prev => ({ ...prev, [groupKey]: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddOrtaklik(groupKey)}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleAddOrtaklik(groupKey)}
                  disabled={!newOrtaklikInputs[groupKey]}
                >
                  Ekle
                </Button>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={1}>
                {group.ortakliklar.map((ortaklik, index) => (
                  <Chip
                    key={index}
                    label={ortaklik}
                    onDelete={() => handleDeleteOrtaklik(groupKey, index)}
                    size="small"
                  />
                ))}
              </Box>
            </Paper>
          ))}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              JSON Konfigürasyonu
            </Typography>
            <pre style={{ 
              background: '#f5f5f5', 
              padding: '16px', 
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '400px'
            }}>
              {JSON.stringify(editedConfig, null, 2)}
            </pre>
          </Paper>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{
        background: 'linear-gradient(90deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
        borderRadius: '0 0 20px 20px',
        p: 3,
        gap: 2
      }}>
        <Button 
          onClick={onClose} 
          startIcon={<Cancel />}
          sx={{
            borderRadius: '12px',
            fontWeight: 'bold',
            textTransform: 'none',
            border: '2px solid #f44336',
            color: '#f44336',
            '&:hover': {
              background: 'linear-gradient(45deg, #f44336, #d32f2f)',
              color: 'white',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.3s ease'
          }}
        >
          ❌ İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<Save />}
          disabled={updateConfigMutation.isPending}
          sx={{
            background: 'linear-gradient(45deg, #4caf50, #45a049)',
            borderRadius: '12px',
            fontWeight: 'bold',
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #45a049, #4caf50)',
              transform: 'scale(1.05)',
            },
            '&:disabled': {
              background: '#ccc',
              color: '#999'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {updateConfigMutation.isPending ? '💾 Kaydediliyor...' : '✅ Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfigEditor;
